/**
 * Delivery-schedule cutoff math — THE single shared implementation.
 *
 * Exact port of the old WMS `assertWithinOrderByCutoff` + Stockholm-TZ helpers
 * (`/Users/victor/Dev/WMS/gigachop-wms-backend/apps/orders/src/app/services/
 *  orders-sales-order.service.ts:1931-2115`) and its client mirror
 * (`gigachop-wms-frontend/src/utils/delivery-cutoff.ts`), per ARCHITECTURE §8.5.
 *
 * Why it lives in `packages/shared`: the old system kept two copies of this math
 * (api + web) and they drifted — ARCHITECTURE §12. The API is
 * server-authoritative; the web imports the SAME functions purely to grey out
 * calendar dates. Never fork it.
 *
 * Semantics preserved from the port:
 *  - Wall-clock arithmetic in `Europe/Stockholm` (DST-correct), never UTC.
 *  - Two delivery cycles per restaurant per week; optional per-category override rows.
 *  - **Fail-open**: no schedule row / delivery weekday matches no cycle / no
 *    cutoff time / malformed cutoff time ⇒ no restriction (old §6.6).
 *  - Admins may bypass on UPDATE (permission-derived server-side, never a client flag).
 *
 * Two DELIBERATE fixes over the old implementation:
 *  1. Delivery dates are modelled as bare `YYYY-MM-DD` calendar dates and
 *     deadlines as instants, so the "highlight shifted one weekday after local
 *     midnight" bug class (old §6.5) cannot occur.
 *  2. `delivery_schedule_custom` (holiday moves) is honoured HERE, on the
 *     server: a moved date inherits the cutoff of the date it replaced, and the
 *     date it replaced stops being orderable. The old system merged custom
 *     dates into the calendar *after* the cutoff filter and the backend knew
 *     nothing about them, so a custom date bypassed the cutoff entirely (§6.1).
 */

export const DELIVERY_CUTOFF_TIMEZONE = 'Europe/Stockholm';

/** How far ahead the portal calendar highlights, matching the old WMS. */
export const DELIVERY_CALENDAR_WINDOW_DAYS = 120;

/** ISO weekday, 1 = Monday … 7 = Sunday (storage form; the UI shows 1-5). */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** One (delivery day, order-by day, order-by time) cycle. */
export interface DeliveryCycle {
  deliveryDay: Weekday;
  dueDay: Weekday;
  /** `HH:MM` or `HH:MM:SS` wall-clock in Europe/Stockholm; null = end of dueDay. */
  dueTime: string | null;
}

/** A `delivery_schedules` row, category-scoped when `categoryId` is set. */
export interface DeliveryScheduleRule {
  restaurantId: string;
  /** null = the restaurant default rule. */
  categoryId: string | null;
  cycles: DeliveryCycle[];
}

/** A `delivery_schedule_custom` row: `changedDate` is moved to `newDate`. */
export interface DeliveryDateOverride {
  restaurantId: string;
  /** `YYYY-MM-DD` */
  changedDate: string;
  /** `YYYY-MM-DD` */
  newDate: string;
}

export interface CutoffInput {
  /** `YYYY-MM-DD` the customer wants delivered. */
  deliveryDate: string;
  rules: DeliveryScheduleRule[];
  overrides?: DeliveryDateOverride[];
  /** Article categories in the order — selects override rules. */
  categoryIds?: string[];
  /** Evaluation instant; defaults to `new Date()`. Injectable for tests. */
  now?: Date;
}

export interface CutoffResult {
  allowed: boolean;
  /** i18n error code when `allowed === false`. */
  code?: 'E_ORDER_BY_CUTOFF';
  params?: {
    deliveryDate?: string;
    dueAt?: string;
    nextDeliveryDate?: string;
  };
  /** The instant the order had to be placed by, ISO-8601, when derivable. */
  dueAt?: string | null;
  /** Set when the answer is "no rules configured" — the fail-open path. */
  failedOpen?: boolean;
}

/**
 * Flag read by callers (and by M4's own tests) so nothing silently depends on
 * placeholder behaviour.
 */
export const DELIVERY_CUTOFF_IMPLEMENTED = true;

// ---------------------------------------------------------------------------
// calendar primitives — a delivery date is a LocalDate, never an instant
// ---------------------------------------------------------------------------

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDate(value: string): boolean {
  return ISO_DATE_RE.test(value);
}

/** `YYYY-MM-DD` → UTC-midnight epoch ms (a stable handle on a calendar date). */
export function isoDateToUtcMs(date: string): number {
  const m = ISO_DATE_RE.exec(date);
  if (!m) return Number.NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** UTC-midnight epoch ms → `YYYY-MM-DD`. */
export function utcMsToIsoDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function addIsoDays(date: string, days: number): string {
  return utcMsToIsoDate(isoDateToUtcMs(date) + days * 86_400_000);
}

/** JS weekday (0 = Sunday … 6 = Saturday) of a calendar date, TZ-independent. */
export function isoDateWeekday(date: string): number {
  return new Date(isoDateToUtcMs(date)).getUTCDay();
}

/** ISO weekday 1-7 → JS weekday 0-6, exactly as the old backend did (`d % 7`). */
export function toJsWeekday(day: number): number {
  return day % 7;
}

/** Today's calendar date in Europe/Stockholm for an instant. */
export function stockholmToday(now: Date = new Date()): string {
  const p = stockholmDateParts(now);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}`;
}

/** Europe/Stockholm calendar parts (year, 0-based month, day, JS weekday). */
export function stockholmDateParts(date: Date): {
  year: number;
  month: number;
  day: number;
  weekday: number;
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DELIVERY_CUTOFF_TIMEZONE,
    hour12: false,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});

  const wdMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  return {
    year: Number(parts['year']),
    month: Number(parts['month']) - 1,
    day: Number(parts['day']),
    weekday: wdMap[parts['weekday'] ?? 'Sun'] ?? 0,
  };
}

/**
 * The UTC instant (ms) of a Europe/Stockholm wall-clock, DST-safe. Iterates to
 * settle the zone offset (which differs across DST boundaries) — the same
 * two-pass trick the old backend used, kept because JS still has no zoned
 * datetime constructor.
 */
export function stockholmWallClockToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: DELIVERY_CUTOFF_TIMEZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const target = Date.UTC(year, month, day, hour, minute, 0);
  let utc = target;
  for (let i = 0; i < 3; i += 1) {
    const p = dtf.formatToParts(new Date(utc)).reduce<Record<string, number>>((acc, x) => {
      if (x.type !== 'literal') acc[x.type] = Number(x.value);
      return acc;
    }, {});
    // `hour: '2-digit'` with hour12:false renders midnight as 24 in some ICU
    // versions — normalise so the diff below is never a full day out.
    const hour24 = (p['hour'] ?? 0) % 24;
    const wall = Date.UTC(
      p['year'] ?? year,
      (p['month'] ?? 1) - 1,
      p['day'] ?? day,
      hour24,
      p['minute'] ?? 0,
      p['second'] ?? 0,
    );
    const diff = target - wall;
    if (diff === 0) break;
    utc += diff;
  }
  return utc;
}

/** `HH:MM` / `HH:MM:SS` (postgres returns the latter) → parts, or null. */
export function parseCutoffTime(raw: string | null | undefined): { hour: number; minute: number } | null {
  if (!raw) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(raw);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/** `HH:MM` for form fields — postgres `time` comes back as `HH:MM:SS`. */
export function normalizeCutoffTime(raw: string | null | undefined): string | null {
  const parsed = parseCutoffTime(raw);
  if (!parsed) return null;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(parsed.hour)}:${pad(parsed.minute)}`;
}

// ---------------------------------------------------------------------------
// rule resolution
// ---------------------------------------------------------------------------

/**
 * Which rules govern an order (old §3.2): a row matching the category wins,
 * otherwise the restaurant default (`categoryId === null`). With several
 * categories in one order every applicable rule is returned and the MOST
 * RESTRICTIVE one decides — the old system only ever passed a single category.
 */
export function applicableDeliveryRules(
  rules: readonly DeliveryScheduleRule[],
  categoryIds?: readonly string[] | null,
): DeliveryScheduleRule[] {
  const fallback = rules.find((r) => r.categoryId === null);
  if (!categoryIds || categoryIds.length === 0) return fallback ? [fallback] : [];

  const byCategory = new Map<string, DeliveryScheduleRule>();
  for (const rule of rules) if (rule.categoryId) byCategory.set(rule.categoryId, rule);

  const out: DeliveryScheduleRule[] = [];
  for (const categoryId of new Set(categoryIds)) {
    const rule = byCategory.get(categoryId) ?? fallback;
    if (rule && !out.includes(rule)) out.push(rule);
  }
  return out;
}

/** The cycle whose delivery weekday matches, cycle 1 first (old §3.5). */
export function cycleForWeekday(
  rule: DeliveryScheduleRule,
  jsWeekday: number,
): DeliveryCycle | null {
  for (const cycle of rule.cycles) {
    if (toJsWeekday(cycle.deliveryDay) === jsWeekday) return cycle;
  }
  return null;
}

export function isDeliveryWeekday(rule: DeliveryScheduleRule, jsWeekday: number): boolean {
  return cycleForWeekday(rule, jsWeekday) !== null;
}

/**
 * The order-by deadline (UTC ms) for a delivery date under one rule, or `null`
 * when there is no cutoff (not a delivery day for this rule, or the cycle has
 * no usable cutoff time — both fail open, old §6.6).
 */
export function orderByDeadlineMs(deliveryDate: string, rule: DeliveryScheduleRule): number | null {
  if (!isIsoDate(deliveryDate)) return null;
  const cycle = cycleForWeekday(rule, isoDateWeekday(deliveryDate));
  if (!cycle) return null;
  const time = parseCutoffTime(cycle.dueTime);
  if (!time) return null;

  // Walk backwards (calendar arithmetic) to the order-by weekday in the same
  // delivery week, then read that date + cutoff time as Stockholm wall-clock.
  const target = toJsWeekday(cycle.dueDay);
  let cursor = deliveryDate;
  for (let i = 0; i < 7 && isoDateWeekday(cursor) !== target; i += 1) {
    cursor = addIsoDays(cursor, -1);
  }
  const m = ISO_DATE_RE.exec(cursor);
  if (!m) return null;
  return stockholmWallClockToUtcMs(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    time.hour,
    time.minute,
  );
}

/**
 * How many days before its delivery the cutoff on `weekday` (ISO 1-7) falls —
 * the "senast N dagar innan" caption in the admin grid (old §3.6).
 * Display only; never used in enforcement.
 */
export function dueLeadDays(rule: DeliveryScheduleRule, isoWeekday: number): number | null {
  const normalize = (d: number): number => d % 7 || 7;
  for (const cycle of rule.cycles) {
    if (cycle.dueDay !== isoWeekday) continue;
    let diff = normalize(cycle.deliveryDay) - normalize(isoWeekday);
    if (diff <= 0) diff += 7;
    return diff;
  }
  return null;
}

// ---------------------------------------------------------------------------
// enforcement (server-authoritative) — the port of assertWithinOrderByCutoff
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD HH:MM` in Stockholm — what the E_ORDER_BY_CUTOFF message shows. */
export function formatStockholmDeadline(instantMs: number): string {
  const p = stockholmDateParts(new Date(instantMs));
  const time = new Intl.DateTimeFormat('sv-SE', {
    timeZone: DELIVERY_CUTOFF_TIMEZONE,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(instantMs));
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)} ${time}`;
}

export function checkOrderByCutoff(input: CutoffInput): CutoffResult {
  const now = input.now ?? new Date();
  const rules = input.rules;
  const overrides = input.overrides ?? [];

  // No schedule at all ⇒ no restriction whatsoever (old §3.2 step 3).
  if (rules.length === 0) return { allowed: true, failedOpen: true, dueAt: null };

  // A date that has been MOVED away is no longer a delivery date. This is a
  // deliberate admin action, so it denies rather than failing open.
  const movedAway = overrides.find((o) => o.changedDate === input.deliveryDate);
  if (movedAway) {
    return {
      allowed: false,
      code: 'E_ORDER_BY_CUTOFF',
      params: { deliveryDate: input.deliveryDate, nextDeliveryDate: movedAway.newDate },
      dueAt: null,
    };
  }

  // A replacement date inherits the cutoff of the date it replaced — otherwise
  // a holiday move would silently hand out extra ordering time (old §6.1).
  const replacement = overrides.find((o) => o.newDate === input.deliveryDate);
  const evaluationDate = replacement ? replacement.changedDate : input.deliveryDate;

  const applicable = applicableDeliveryRules(rules, input.categoryIds);
  if (applicable.length === 0) return { allowed: true, failedOpen: true, dueAt: null };

  let deadline: number | null = null;
  for (const rule of applicable) {
    const candidate = orderByDeadlineMs(evaluationDate, rule);
    if (candidate === null) continue;
    if (deadline === null || candidate < deadline) deadline = candidate;
  }
  if (deadline === null) return { allowed: true, dueAt: null };

  const dueAt = new Date(deadline).toISOString();
  if (now.getTime() <= deadline) return { allowed: true, dueAt };

  return {
    allowed: false,
    code: 'E_ORDER_BY_CUTOFF',
    params: {
      deliveryDate: input.deliveryDate,
      dueAt: formatStockholmDeadline(deadline),
    },
    dueAt,
  };
}

/** Convenience mirror of the old client helper. */
export function isDeliveryDateOrderable(input: CutoffInput): boolean {
  return checkOrderByCutoff(input).allowed;
}

// ---------------------------------------------------------------------------
// calendar (portal + admin)
// ---------------------------------------------------------------------------

export interface SelectableDeliveryDate {
  /** `YYYY-MM-DD` */
  date: string;
  /** ISO instant the order must be placed by; null = no cutoff time configured. */
  dueAt: string | null;
  /** Set when this date replaces an originally scheduled one (holiday move). */
  movedFrom?: string;
}

export interface DeliveryCalendarInput {
  rules: DeliveryScheduleRule[];
  overrides?: DeliveryDateOverride[];
  categoryIds?: string[];
  now?: Date;
  /** Defaults to 120, matching the old WMS. */
  windowDays?: number;
}

export interface DeliveryCalendarResult {
  /** Dates the restaurant may still order for, ascending. */
  dates: SelectableDeliveryDate[];
  /** Delivery days inside the window whose order-by deadline has passed. */
  passed: string[];
  /** Dates removed because an admin moved them (holiday). */
  moved: DeliveryDateOverride[];
  /** True when the restaurant has no rule that applies — the UI blocks submit. */
  scheduleMissing: boolean;
  /** First date of the window (tomorrow, Stockholm). */
  windowStart: string;
  windowEnd: string;
}

/**
 * The portal calendar: delivery days in a 120-day window that STARTS TOMORROW
 * (same-day delivery is never orderable — old `DeliveryCalendar.tsx:54-56`),
 * with the passed-cutoff dates filtered out and holiday moves applied.
 */
export function buildDeliveryCalendar(input: DeliveryCalendarInput): DeliveryCalendarResult {
  const now = input.now ?? new Date();
  const windowDays = input.windowDays ?? DELIVERY_CALENDAR_WINDOW_DAYS;
  const windowStart = addIsoDays(stockholmToday(now), 1);
  const windowEnd = addIsoDays(windowStart, Math.max(0, windowDays - 1));
  const overrides = (input.overrides ?? []).filter((o) => isIsoDate(o.changedDate) && isIsoDate(o.newDate));

  const applicable = applicableDeliveryRules(input.rules, input.categoryIds);
  if (applicable.length === 0) {
    return {
      dates: [],
      passed: [],
      moved: [],
      scheduleMissing: true,
      windowStart,
      windowEnd,
    };
  }

  const movedAway = new Map(overrides.map((o) => [o.changedDate, o] as const));
  const dates: SelectableDeliveryDate[] = [];
  const passed: string[] = [];
  const moved: DeliveryDateOverride[] = [];
  const seen = new Set<string>();

  const consider = (deliveryDate: string, evaluationDate: string, movedFrom?: string): void => {
    if (seen.has(deliveryDate)) return;
    // Every applicable rule must accept the date: an order spanning two
    // categories can only be delivered on a day both categories deliver.
    let deadline: number | null = null;
    for (const rule of applicable) {
      if (!isDeliveryWeekday(rule, isoDateWeekday(evaluationDate))) return;
      const candidate = orderByDeadlineMs(evaluationDate, rule);
      if (candidate === null) continue;
      if (deadline === null || candidate < deadline) deadline = candidate;
    }
    seen.add(deliveryDate);
    if (deadline !== null && now.getTime() > deadline) {
      passed.push(deliveryDate);
      return;
    }
    dates.push({
      date: deliveryDate,
      dueAt: deadline === null ? null : new Date(deadline).toISOString(),
      ...(movedFrom ? { movedFrom } : {}),
    });
  };

  for (let i = 0; i < windowDays; i += 1) {
    const date = addIsoDays(windowStart, i);
    const override = movedAway.get(date);
    if (override) {
      moved.push(override);
      continue;
    }
    consider(date, date);
  }

  // Replacement dates: orderable up to the ORIGINAL date's deadline, and only
  // if they still lie ahead of us.
  for (const override of overrides) {
    if (override.newDate < windowStart || override.newDate > windowEnd) continue;
    if (!moved.some((m) => m.changedDate === override.changedDate)) {
      // The moved-from date is outside the window; the replacement is still valid.
      moved.push(override);
    }
    consider(override.newDate, override.changedDate, override.changedDate);
  }

  dates.sort((a, b) => a.date.localeCompare(b.date));
  passed.sort((a, b) => a.localeCompare(b));

  return { dates, passed, moved, scheduleMissing: false, windowStart, windowEnd };
}

/**
 * The set of dates a restaurant may pick in the portal calendar (old WMS used a
 * 120-day window starting tomorrow).
 */
export function listSelectableDeliveryDates(input: DeliveryCalendarInput): string[] {
  return buildDeliveryCalendar(input).dates.map((d) => d.date);
}
